<template>
  <div class="profit-calculator">
    <h2>期权收益计算器</h2>
    <div class="input-group">
      <label for="initialPrice">期初价格 (元): </label>
      <input type="number" id="initialPrice" v-model.number="initialPrice" step="0.01" />
    </div>
    <div class="input-group">
      <label>期权结构: </label>
      <div class="radio-group">
        <label class="radio-label">
          <input type="radio" v-model="selectedThreshold" value="1.03" />
          <span class="radio-custom"></span>
          103%
        </label>
        <label class="radio-label">
          <input type="radio" v-model="selectedThreshold" value="1.05" />
          <span class="radio-custom"></span>
          105%
        </label>
        <label class="radio-label">
          <input type="radio" v-model="selectedThreshold" value="1.10" />
          <span class="radio-custom"></span>
          110%
        </label>
      </div>
    </div>
    <div class="input-group">
      <label for="profitThresholds">期权费率 (格式: 8.45): </label>
      <input type="text" id="profitThresholds" v-model="profitThresholdsInput" />
    </div>
    <button @click="parseProfitThresholds">开始计算</button>
    <div v-for="(premiumRate, profitThreshold) in profitThresholds" :key="profitThreshold" class="result-section">
      <h3>期权结构: {{ (parseFloat(profitThreshold) * 100).toFixed(0) }}%, 期权费率: {{ premiumRate }}%</h3>
      <ul>
        <li v-for="closingPrice in closingPrices(parseFloat(profitThreshold))" :key="closingPrice" class="result-item">
          平仓价: {{ closingPrice.toFixed(2) }}元, 最终收益: {{ calculateProfit(initialPrice, strikePrice(parseFloat(profitThreshold)), notionalPrincipal, parseFloat(premiumRate), closingPrice, parseFloat(profitThreshold)).toFixed(2) }}万元
        </li>
      </ul>
      <hr>
    </div>
    <div v-if="selectedThreshold" class="result-section">
      <h3>当前选中的期权结构: {{ (parseFloat(selectedThreshold) * 100).toFixed(0) }}%</h3>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      initialPrice: 16.2, // 期初价格 (元)
      notionalPrincipal: 100, // 名义本金 (万元)
      selectedThreshold: '1.03', // 默认选中的期权结构
      profitThresholds: {}, // 期权结构和费率
      profitThresholdsInput: '' // 输入的期权费率字符串
    };
  },
  methods: {
    calculateProfit(initialPrice, strikePrice, notionalPrincipal, premiumRate, closingPrice, profitThreshold) {
      // 计算期权费
      const premium = notionalPrincipal * (premiumRate / 100);

      // 计算最终收益
      let profit;
      if (closingPrice <= strikePrice) {
        profit = -premium;
      } else if (closingPrice <= strikePrice * profitThreshold) {
        // 线性插值计算收益
        const maxLoss = -premium;
        const maxGain = 0;
        const minClosingPrice = strikePrice;
        const maxClosingPrice = strikePrice * profitThreshold;

        // 计算收益
        profit = maxLoss + ((closingPrice - minClosingPrice) / (maxClosingPrice - minClosingPrice)) * (maxGain - maxLoss);
      } else {
        profit = ((closingPrice - strikePrice) / initialPrice) * notionalPrincipal - premium;
      }
      console.log('Calculated profit:', profit);
      return profit;
    },
    strikePrice(profitThreshold) {
      return this.initialPrice * profitThreshold;
    },
    closingPrices(profitThreshold) {
      const strikePrice = this.strikePrice(profitThreshold);
      return [
        strikePrice,
        strikePrice + 1,
        strikePrice + 2,
        strikePrice + 3,
        strikePrice + 4,
        strikePrice + 5,
        strikePrice + 6,
        strikePrice + 7
      ];
    },
    parseProfitThresholds() {
      try {
        // 假设输入的格式是 "8.45"
        const premiumRate = parseFloat(this.profitThresholdsInput);
        if (isNaN(premiumRate)) {
          console.error('Invalid premium rate:', this.profitThresholdsInput);
          return;
        }

        // 创建一个对象来存储解析后的阈值和费率
        this.profitThresholds = {
          [this.selectedThreshold]: premiumRate
        };

        console.log('Parsed profit thresholds:', this.profitThresholds);
      } catch (error) {
        console.error('Failed to parse profit thresholds:', error);
      }
    }
  },
  watch: {
    selectedThreshold(newVal) {
      console.log('Selected Threshold:', newVal);
    }
  }
};
</script>

<style scoped>
/* 添加一些样式 */
.profit-calculator {
  max-width: 600px;
  margin: 0 auto;
  padding: 20px;
  font-family: Arial, sans-serif;
  background-color: #f9f9f9;
  border-radius: 8px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
}

h2 {
  text-align: center;
  color: #333;
  margin-bottom: 20px;
}

.input-group {
  margin-bottom: 15px;
  display: flex;
  align-items: center;
}

.input-group label {
  margin-right: 10px;
  flex: 1;
  font-weight: bold;
}

.radio-group {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 10px;
}

.radio-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  position: relative;
}

.radio-label input[type="radio"] {
  display: none;
}

.radio-custom {
  width: 18px;
  height: 18px;
  background-color: #fff;
  border: 2px solid #ccc;
  border-radius: 50%;
  margin-right: 10px;
  position: relative;
  transition: border-color 0.3s;
}

.radio-label input[type="radio"]:checked + .radio-custom::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 10px;
  height: 10px;
  background-color: #007bff;
  border-radius: 50%;
}

.radio-label:hover .radio-custom {
  border-color: #007bff;
}

input[type="number"], input[type="text"] {
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  flex: 2;
  transition: border-color 0.3s;
}

input[type="number"]:focus, input[type="text"]:focus {
  border-color: #007bff;
  outline: none;
}

button {
  padding: 10px 15px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;
}

button:hover {
  background-color: #0056b3;
}

.result-section {
  margin-bottom: 20px;
  padding: 10px;
  background-color: #fff;
  border-radius: 4px;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.1);
}

.result-section h3 {
  margin-bottom: 10px;
  color: #333;
}

.result-item {
  margin-bottom: 5px;
  color: #555;
}

hr {
  border: none;
  border-top: 1px solid #ddd;
  margin: 20px 0;
}
</style>
